import { useState, type JSX } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Carregando, Erro, Vazio } from '../componentes/Estados.js';
import { SeletorPeriodo } from '../componentes/SeletorPeriodo.js';
import { obter } from '../lib/api.js';
import {
  CODIGOS_PARA_FILTRO,
  dataHoraBr,
  ehGrave,
  frase,
  type EntradaLog,
} from '../lib/logs-frases.js';
import { periodoIncompleto, periodoInvertido, ultimosDias, type Periodo } from '../lib/periodo.js';
import './Logs.css';

/**
 * Trilha de atividade — Frente L, Story L.4.
 *
 * Consome `GET /api/logs` (L.3), que já unifica `audit_log` e `security_event`,
 * pagina por cursor e filtra o que não pode sair.
 *
 * ## A tela CONFIA na allowlist da API
 *
 * O detalhe expandido mostra o que veio, e nada além. Não reconstrói campo que
 * a API omitiu nem busca o objeto original para "completar" o registro — se
 * fizesse isso, a allowlist do servidor viraria decoração, contornada pela
 * primeira tela que quisesse mostrar mais.
 *
 * ## Só admin
 *
 * `ver_logs` ficou apenas com o admin na L.3. O item de menu e a rota seguem a
 * mesma regra; a guarda de verdade é o 403 do servidor.
 */

interface PaginaLogs {
  itens: EntradaLog[];
  proximoCursor: string | null;
}

/** O detalhe de uma linha — só o que a API mandou. */
function Detalhe({ entrada }: { entrada: EntradaLog }): JSX.Element {
  const { antes, depois } = entrada.detalhe;
  const nada = antes === undefined && depois === undefined;

  return (
    <div className="logs__detalhe">
      {nada ? (
        <p className="logs__vazio-detalhe">Este registro não guarda detalhe.</p>
      ) : (
        <div className="logs__colunas">
          {antes !== undefined && <Bloco titulo="Antes" dados={antes} />}
          {depois !== undefined && <Bloco titulo="Depois" dados={depois} />}
        </div>
      )}
      <dl className="logs__meta">
        <dt>Origem</dt>
        <dd>{entrada.origem === 'audit' ? 'Alteração de dado' : 'Evento de segurança'}</dd>
        <dt>Código</dt>
        {/*
          O código CRU aparece aqui, e só aqui: quem precisa dele está
          depurando, e quem está lendo a trilha quer a frase. Num registro de
          formato antigo, `codigoBruto` difere de `codigo` e é o que explica o
          rótulo "(registro antigo)".
        */}
        <dd className="logs__codigo">
          {entrada.codigo}
          {entrada.codigoBruto !== entrada.codigo && ` (gravado como "${entrada.codigoBruto}")`}
        </dd>
        {entrada.entidadeId !== null && (
          <>
            <dt>Alvo</dt>
            <dd className="logs__codigo">{entrada.entidadeId}</dd>
          </>
        )}
      </dl>
    </div>
  );
}

function Bloco({ titulo, dados }: { titulo: string; dados: Record<string, unknown> }): JSX.Element {
  return (
    <div className="logs__bloco">
      <h4>{titulo}</h4>
      <dl>
        {Object.entries(dados).map(([k, v]) => (
          <div key={k} className="logs__par">
            <dt>{k}</dt>
            <dd>{v === null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Linha({ entrada }: { entrada: EntradaLog }): JSX.Element {
  const [aberta, setAberta] = useState(false);
  const grave = ehGrave(entrada);

  return (
    <li className={grave ? 'logs__linha logs__linha--grave' : 'logs__linha'}>
      <button
        type="button"
        className="logs__cabecalho"
        aria-expanded={aberta}
        onClick={() => {
          setAberta((a) => !a);
        }}
      >
        <span className="logs__quando num">{dataHoraBr(entrada.quando)}</span>
        <span className="logs__quem">{entrada.autorNome ?? 'sistema'}</span>
        <span className="logs__frase">{frase(entrada)}</span>
        <span className="logs__seta" aria-hidden="true">
          {aberta ? '−' : '+'}
        </span>
      </button>
      {aberta && <Detalhe entrada={entrada} />}
    </li>
  );
}

export function Logs(): JSX.Element {
  const [periodo, setPeriodo] = useState<Periodo>(() => ultimosDias(new Date(), 7));
  const [codigo, setCodigo] = useState('');
  const [incluirRotina, setIncluirRotina] = useState(false);

  const incompleto = periodoIncompleto(periodo);
  const invertido = periodoInvertido(periodo);
  const podeBuscar = !incompleto && !invertido;

  /*
   * `useInfiniteQuery` porque a L.3 pagina por CURSOR, não por offset — e o
   * cursor de cada página só existe depois de a anterior chegar. Um `useQuery`
   * com página no estado teria de guardar a lista acumulada à mão e reconciliar
   * na troca de filtro, que é exatamente o que este hook faz.
   */
  const consulta = useInfiniteQuery({
    /* Os filtros entram na chave: sem eles, trocar de filtro mostraria a lista
       anterior sob o recorte novo. */
    queryKey: ['logs', periodo.de, periodo.ate, codigo, incluirRotina],
    initialPageParam: '',
    queryFn: async ({ pageParam, signal }) => {
      const q = new URLSearchParams({ de: periodo.de, ate: periodo.ate, limite: '50' });
      if (codigo !== '') q.set('codigo', codigo);
      /* Só manda quando LIGADO — a API já exclui a rotina por padrão, e mandar
         `incluirRotina=0` seria mandar `true` (a rota aceita '1'|'true'). */
      if (incluirRotina) q.set('incluirRotina', '1');
      if (typeof pageParam === 'string' && pageParam !== '') q.set('cursor', pageParam);
      return obter<PaginaLogs>(`/api/logs?${q.toString()}`, signal);
    },
    getNextPageParam: (ultima: PaginaLogs) => ultima.proximoCursor ?? undefined,
    enabled: podeBuscar,
  });

  const entradas = consulta.data?.pages.flatMap((p) => p.itens) ?? [];

  return (
    <section className="pagina logs">
      <header className="pagina__cabecalho">
        <div>
          <h1>Atividades</h1>
          <p className="pagina__sub">
            Quem fez o quê, e quando. Reúne alterações de dado e eventos de segurança.
          </p>
        </div>
      </header>

      <div className="cartao logs__filtros">
        <SeletorPeriodo periodo={periodo} aoMudar={setPeriodo} />

        <div className="logs__linha-filtro">
          <label className="campo logs__campo">
            <span className="campo__rotulo">Tipo de ação</span>
            <select
              className="campo__entrada"
              value={codigo}
              onChange={(e) => {
                setCodigo(e.target.value);
              }}
            >
              <option value="">Todas</option>
              {CODIGOS_PARA_FILTRO.map((c) => (
                <option key={c.codigo} value={c.codigo}>
                  {c.rotulo}
                </option>
              ))}
            </select>
          </label>

          {/*
            Desligado por padrão. A L.1 mediu que `refresh_ok` era 44% de todos
            os eventos — ligado, ele empurra para fora da primeira página
            justamente o que se veio procurar.
          */}
          <label className="logs__toggle">
            <input
              type="checkbox"
              checked={incluirRotina}
              onChange={(e) => {
                setIncluirRotina(e.target.checked);
              }}
            />
            <span>Incluir eventos de rotina (renovação de sessão)</span>
          </label>
        </div>
      </div>

      {!podeBuscar ? (
        <div className="cartao">
          <Vazio titulo="Ajuste o período" descricao="Escolha um intervalo válido." />
        </div>
      ) : consulta.isPending ? (
        <div className="cartao">
          <Carregando rotulo="Carregando atividades…" />
        </div>
      ) : consulta.isError ? (
        <div className="cartao">
          <Erro
            erro={consulta.error}
            aoRepetir={() => {
              void consulta.refetch();
            }}
          />
        </div>
      ) : entradas.length === 0 ? (
        <div className="cartao">
          <Vazio
            titulo="Nenhuma atividade neste período"
            descricao={`Nada foi registrado entre ${periodo.de} e ${periodo.ate} com os filtros escolhidos. Amplie o período ou tire o filtro de tipo.`}
          />
        </div>
      ) : (
        <div className="cartao logs__quadro">
          <ul className="logs__lista">
            {entradas.map((e) => (
              <Linha key={e.id} entrada={e} />
            ))}
          </ul>

          {consulta.hasNextPage && (
            <div className="logs__mais">
              <button
                type="button"
                className="botao botao--neutro"
                disabled={consulta.isFetchingNextPage}
                onClick={() => {
                  void consulta.fetchNextPage();
                }}
              >
                {consulta.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
              </button>
            </div>
          )}
          {!consulta.hasNextPage && (
            <p className="logs__fim">Fim da lista — {entradas.length} registro(s).</p>
          )}
        </div>
      )}
    </section>
  );
}
